/**
 * Modèle d'accès de l'Extranet API v1.12.0.
 *
 * Deux notions distinctes, à ne pas confondre :
 *
 * - **Permissions** (`/me.permissions`) — ce que *l'appelant* peut faire. C'est
 *   la seule source du gating : menus, routes, actions. Les rôles ne servent
 *   plus à ça depuis la v1.12.0.
 * - **Rôles** (`RoleGrant`) — ce qu'un *admin attribue* à un membre, avec son
 *   périmètre. Modèle inchangé, utilisé par l'écran Administration
 *   (`GET /v1/users`, `PATCH /v1/users/:id`, invitations).
 *
 * Aucun import : ce module doit rester importable de partout.
 */

// ─── Permissions : le gating ────────────────────────────────

export const EXTRANET_PERMISSIONS = [
  /** Accès au portail lui-même — sans elle le back refuse tout en 403. */
  'extranet-access',
  'extranet-admin',
  'extranet-billing',
  'extranet-trips',
  'extranet-incidents',
  'extranet-performance',
  'extranet-exports',
] as const;
export type ExtranetPermission = (typeof EXTRANET_PERMISSIONS)[number];

/**
 * Page d'atterrissage par permission, par ordre de préférence. `extranet-access`
 * n'y figure pas : elle n'ouvre aucun écran, elle donne accès au portail.
 */
export const PERMISSION_HOME: ReadonlyArray<readonly [ExtranetPermission, string]> = [
  ['extranet-trips', '/'],
  ['extranet-billing', '/invoices'],
  ['extranet-incidents', '/incidents'],
  // Les indicateurs vivent sur le tableau de bord : la permission Performance y
  // mène, elle n'a plus d'écran à elle depuis que Performance est devenu Export.
  ['extranet-performance', '/'],
  ['extranet-exports', '/exports'],
  ['extranet-admin', '/admin'],
];

/** Première page ouverte à ces permissions, ou `null` si aucune. */
export const firstAccessiblePath = (permissions: ReadonlySet<string>): string | null =>
  PERMISSION_HOME.find(([permission]) => permissions.has(permission))?.[1] ?? null;

// ─── Rôles : l'attribution d'accès à un membre ──────────────

export const EXTRANET_ROLES = [
  'Extranet_Admin',
  'Extranet_Achat',
  'Extranet_Commande',
  'Extranet_Facturation',
] as const;
export type ExtranetRole = (typeof EXTRANET_ROLES)[number];

export const SCOPE_KEYS = [
  'billingReference1',
  'billingReference2',
  'billingReference3',
  'costCenter',
  'serial',
] as const;
export type ScopeKey = (typeof SCOPE_KEYS)[number];

export interface RoleScope {
  key: ScopeKey;
  values: string[];
}

/**
 * Un rôle et son périmètre. `scopes: []` = tout le compte.
 * `role` reste un `string` comme dans le contrat : un rôle inconnu du front
 * doit être conservé tel quel (cf. `normalizeAccess`), pas rejeté.
 */
export interface RoleGrant {
  role: string;
  scopes: RoleScope[];
}

/**
 * Domaines fonctionnels — utilisés **uniquement pour décrire ce qu'un rôle
 * débloque** dans l'écran d'attribution. Le gating, lui, passe par les
 * permissions.
 */
export const DOMAINS = ['admin', 'invoicing', 'trips', 'incidents', 'performance'] as const;
export type Domain = (typeof DOMAINS)[number];

/** Matrice profil → domaines, publiée par la doc « Rôles & accès ». */
export const ROLE_DOMAINS: Record<ExtranetRole, readonly Domain[]> = {
  Extranet_Admin: ['admin', 'invoicing', 'trips', 'incidents', 'performance'],
  Extranet_Achat: ['invoicing', 'trips', 'incidents', 'performance'],
  Extranet_Commande: ['trips', 'incidents'],
  Extranet_Facturation: ['invoicing'],
};

export const DOMAIN_LABELS: Record<Domain, string> = {
  admin: 'Administration',
  invoicing: 'Facturation',
  trips: 'Courses',
  incidents: 'Incidents',
  performance: 'Performance',
};

export const ROLE_LABELS: Record<ExtranetRole, string> = {
  Extranet_Admin: 'Administrateur',
  Extranet_Achat: 'Achats',
  Extranet_Commande: 'Commande',
  Extranet_Facturation: 'Facturation',
};

export const SCOPE_KEY_LABELS: Record<ScopeKey, string> = {
  billingReference1: 'Réf. facturation 1 (site / BUPO)',
  billingReference2: 'Réf. facturation 2 (contrat / CTR)',
  billingReference3: 'Réf. facturation 3',
  costCenter: 'Centre de coût',
  serial: 'Matricule',
};

export const isKnownRole = (role: string): role is ExtranetRole => (EXTRANET_ROLES as readonly string[]).includes(role);

/** Rôles connus portés par un jeu d'accès, dans l'ordre canonique. */
export const rolesOf = (access: readonly RoleGrant[]): ExtranetRole[] =>
  EXTRANET_ROLES.filter(role => access.some(grant => grant.role === role));

/**
 * Union des périmètres d'un jeu d'accès, valeurs fusionnées par dimension.
 *
 * C'est le plafond de ce qu'un appelant peut déléguer. À ne surtout pas
 * confondre avec `/me.filters` : celui-ci déclare les dimensions de **filtrage
 * de données** (le contrat / CTR par exemple), dont toutes ne sont pas des
 * périmètres attribuables. Seul `/me.access` porte les périmètres.
 */
export const unionScopes = (access: readonly RoleGrant[]): RoleScope[] => {
  const byKey = new Map<ScopeKey, Set<string>>();
  for (const grant of access) {
    for (const scope of grant.scopes) {
      const values = byKey.get(scope.key) ?? new Set<string>();
      scope.values.forEach(value => values.add(value));
      byKey.set(scope.key, values);
    }
  }
  return Array.from(byKey, ([key, values]) => ({ key, values: Array.from(values) }));
};

/** Une dimension de filtrage inconnue du front s'affiche telle quelle plutôt que vide. */
export const scopeKeyLabel = (key: string): string => SCOPE_KEY_LABELS[key as ScopeKey] ?? key;

export const roleLabel = (role: string): string => (isKnownRole(role) ? ROLE_LABELS[role] : role);

export const roleDescription = (role: ExtranetRole): string =>
  ROLE_DOMAINS[role].map(domain => DOMAIN_LABELS[domain]).join(' · ');

export const scopeSummary = (grant: RoleGrant): string => {
  const count = grant.scopes.filter(scope => scope.values.length > 0).length;
  if (count === 0) return 'Tout le compte';
  return count === 1 ? '1 restriction' : `${count} restrictions`;
};

/**
 * Nettoie un jeu d'accès avant envoi : un seul grant par rôle (les périmètres
 * sont fusionnés), valeurs trimées et dédoublonnées, scopes sans valeur retirés.
 */
export const normalizeAccess = (access: readonly RoleGrant[]): RoleGrant[] => {
  const byRole = new Map<string, Map<ScopeKey, Set<string>>>();
  for (const grant of access) {
    const scopes = byRole.get(grant.role) ?? new Map<ScopeKey, Set<string>>();
    for (const scope of grant.scopes) {
      const values = scopes.get(scope.key) ?? new Set<string>();
      scope.values
        .map(value => value.trim())
        .filter(Boolean)
        .forEach(value => values.add(value));
      scopes.set(scope.key, values);
    }
    byRole.set(grant.role, scopes);
  }
  return Array.from(byRole, ([role, scopes]) => ({
    role,
    scopes: Array.from(scopes, ([key, values]) => ({ key, values: Array.from(values) })).filter(
      scope => scope.values.length > 0,
    ),
  }));
};
